'use client';

import { ReactNode, useLayoutEffect, useState } from 'react';

import { useMediaQuery } from 'react-responsive';

import breakPoint from '@utils/theme/breakPoint';

import { createGenericContext } from './generateContext';

type ContextInterface = {
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
};

const xsQueryWidth = parseInt(breakPoint?.TABLET?.split('px')?.[0], 10) ?? 0;

const [useDevice, DeviceContextProvider] =
  createGenericContext<ContextInterface>();

export const useBreakpoint = () => {
  const xxl = useMediaQuery({
    query: `(min-width: ${breakPoint.LARGE_2X})`,
  });
  const xl = useMediaQuery({
    query: `(min-width: ${breakPoint.LARGE_1X})`,
  });
  const lg = useMediaQuery({
    query: `(min-width: ${breakPoint.WIDE_DESKTOP})`,
  });
  const md = useMediaQuery({
    query: `(min-width: ${breakPoint.DESKTOP})`,
  });
  const sm = useMediaQuery({
    query: `(min-width: ${breakPoint.TABLET})`,
  });

  const xs = useMediaQuery({
    query: `(max-width: ${xsQueryWidth - 1}px)`,
  });

  return { xxl, xl, lg, md, sm, xs };
};

const DeviceProvider = ({
  params: { isDesktop, isMobile, isTablet },
  children,
}: {
  params: ContextInterface;
  children: ReactNode;
}): JSX.Element => {
  const { xs, sm, md, xl } = useBreakpoint();
  const [breakpoints, setBreakpoints] = useState<ContextInterface>({
    isDesktop,
    isTablet,
    isMobile,
  });

  useLayoutEffect(() => {
    setBreakpoints({
      isDesktop: (xl && (!sm || !xs)) ?? isDesktop,
      isTablet: (md && !xl) ?? isTablet,
      isMobile: ((sm || xs) && !xl && !md) ?? isMobile,
    });
  }, [xs, sm, md, xl, isDesktop, isMobile, isTablet]);

  return (
    <DeviceContextProvider
      value={{
        isDesktop: breakpoints.isDesktop,
        isTablet: breakpoints.isTablet,
        isMobile: breakpoints.isMobile,
      }}
    >
      {children}
    </DeviceContextProvider>
  );
};
export { useDevice, DeviceProvider };
